// Event Tracking Constants and Types
// Centralized event names and property schemas for header icon analytics tracking

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

export const NAVIGATION_EVENTS = {
  PAGE_VIEW: 'page_view',
  HEADER_ICON_CLICK: 'header_icon_click',
  DROPDOWN_ACTION: 'dropdown_action',
} as const;

// ============================================================================
// NOTIFICATION EVENTS
// ============================================================================

export const NOTIFICATION_EVENTS = {
  NOTIFICATION_CLICK: 'notification_click',
  NOTIFICATION_VIEW: 'notification_view',
  NOTIFICATION_MARK_READ: 'notification_mark_read',
  NOTIFICATION_MARK_UNREAD: 'notification_mark_unread',
  NOTIFICATION_SELECT: 'notification_select',
  NOTIFICATION_BULK_ACTION: 'notification_bulk_action',
  NOTIFICATION_FILTER_CHANGE: 'notification_filter_change',
  NOTIFICATION_PAGINATION: 'notification_pagination',
  NOTIFICATION_REFRESH: 'notification_refresh',
  NOTIFICATION_HELP_CLICK: 'notification_help_click',
  NOTIFICATION_TOUR_CLICK: 'notification_tour_click',
  NOTIFICATION_SETTINGS_CLICK: 'notification_settings_click',
  NOTIFICATION_ALERT_DISMISS: 'notification_alert_dismiss',
  NOTIFICATION_BULK_DETAILS_OPEN: 'notification_bulk_details_open',
  NOTIFICATION_BULK_DETAILS_CLOSE: 'notification_bulk_details_close',
  NOTIFICATION_CARD_INTERACTION: 'notification_card_interaction',
} as const;

// ============================================================================
// USER PROFILE EVENTS
// ============================================================================

export const USER_PROFILE_EVENTS = {
  HELP_ICON_CLICK: 'help_icon_click',
  TOUR_ICON_CLICK: 'tour_icon_click',
  HELP_MODAL_OPEN: 'help_modal_open',
  HELP_MODAL_CLOSE: 'help_modal_close',
  CARD_INTERACTION: 'card_interaction',
  SETTINGS_CHANGE: 'settings_change',
  FORM_SAVE: 'form_save',
  FORM_CANCEL: 'form_cancel',
  PASSWORD_RESET: 'password_reset',
  EMAIL_CHANGE: 'email_change',
  PHONE_CHANGE: 'phone_change',
} as const;

export const HEADER_EVENTS = {
  HEADER_HELP_CLICK: 'header_help_click',
  HEADER_TOUR_CLICK: 'header_tour_click',
} as const;

// ============================================================================
// QUESTIONNAIRE SUMMARY EVENTS
// ============================================================================

export const QUESTIONNAIRE_SUMMARY_EVENTS = {
  HELP_ICON_CLICK: 'questionnaire_summary_help_icon_click',
  HELP_MODAL_OPEN: 'questionnaire_summary_help_modal_open',
  HELP_MODAL_CLOSE: 'questionnaire_summary_help_modal_close',
} as const;

// ============================================================================
// TOUR EVENTS
// ============================================================================

export const TOUR_EVENTS = {
  TOUR_START: 'tour_start',
  TOUR_COMPLETE: 'tour_complete',
  TOUR_SKIP: 'tour_skip',
  TOUR_STEP_ADVANCE: 'tour_step_advance',
  TOUR_STEP_BACK: 'tour_step_back',
  // New events for enhanced tracking
  TOUR_PAUSE: 'tour_pause',
  TOUR_RESUME: 'tour_resume',
  TOUR_AUTO_ADVANCE: 'tour_auto_advance',
  TOUR_STEP_VIEW: 'tour_step_view',
  TOUR_CLOSE: 'tour_close',
} as const;

// ============================================================================
// WATCH LIST EVENTS
// ============================================================================

export const WATCH_LIST_EVENTS = {
  WATCH_LIST_ADD: 'watch_list_add',
  WATCH_LIST_REMOVE: 'watch_list_remove',
  WATCH_LIST_REMOVE_ALL: 'watch_list_remove_all',
} as const;

// ============================================================================
// PARTNER LISTING EVENTS (PadSplit referral listings)
// ============================================================================

export const PARTNER_LISTING_EVENTS = {
  IMPRESSION: 'padsplit_listing_impression',
  VIEWED: 'padsplit_listing_viewed',
  APPLY_CLICKED: 'padsplit_apply_clicked',
  INTERSTITIAL_CONFIRMED: 'padsplit_interstitial_confirmed',
  INTERSTITIAL_DISMISSED: 'padsplit_interstitial_dismissed',
} as const;

// ============================================================================
// BULK ACTIONS EVENTS
// ============================================================================

export const BULK_ACTIONS_EVENTS = {
  BULK_STATUS_MODAL_OPEN: 'bulk_status_modal_open',
  BULK_STATUS_MODAL_CLOSE: 'bulk_status_modal_close',
  BULK_STATUS_UPDATE_START: 'bulk_status_update_start',
  BULK_STATUS_UPDATE_SUCCESS: 'bulk_status_update_success',
  BULK_STATUS_UPDATE_FAILURE: 'bulk_status_update_failure',
  BULK_STATUS_SELECTOR_CHANGE: 'bulk_status_selector_change',
  BULK_STATUS_HELP_DISMISS: 'bulk_status_help_dismiss',
  BULK_STATUS_HELP_SHOW: 'bulk_status_help_show',
  BULK_FILE_OWNER_MODAL_OPEN: 'bulk_file_owner_modal_open',
  BULK_FILE_OWNER_MODAL_CLOSE: 'bulk_file_owner_modal_close',
  BULK_FILE_OWNER_UPDATE_START: 'bulk_file_owner_update_start',
  BULK_FILE_OWNER_UPDATE_SUCCESS: 'bulk_file_owner_update_success',
  BULK_FILE_OWNER_UPDATE_FAILURE: 'bulk_file_owner_update_failure',
  BULK_FILE_OWNER_SELECTOR_CHANGE: 'bulk_file_owner_selector_change',
  BULK_FILE_OWNER_HELP_DISMISS: 'bulk_file_owner_help_dismiss',
  BULK_FILE_OWNER_HELP_SHOW: 'bulk_file_owner_help_show',
  BULK_DATES_MODAL_OPEN: 'bulk_dates_modal_open',
  BULK_DATES_MODAL_CLOSE: 'bulk_dates_modal_close',
  BULK_DATES_UPDATE_START: 'bulk_dates_update_start',
  BULK_DATES_UPDATE_SUCCESS: 'bulk_dates_update_success',
  BULK_DATES_UPDATE_FAILURE: 'bulk_dates_update_failure',
  BULK_DATES_SELECTOR_CHANGE: 'bulk_dates_selector_change',
  BULK_DATES_HELP_SHOW: 'bulk_dates_help_show',
} as const;

// ============================================================================
// USER PROFILE CARD TYPES
// ============================================================================

export const USER_PROFILE_CARDS = {
  ACCOUNT_INFORMATION: 'account_information',
  DIFFERENCE_EXPLANATION: 'difference_explanation',
  CONTACT_INFORMATION: 'contact_information',
  COMMUNICATION_PREFERENCES: 'communication_preferences',
  DAILY_EMAIL_DIGEST: 'daily_email_digest',
  PROPERTY_NOTIFICATIONS: 'property_notifications',
  FILE_OWNER_NOTIFICATIONS: 'file_owner_notifications',
  WATCHED_NOTIFICATIONS: 'watched_notifications',
  HOUSEHOLD_NOTIFICATIONS: 'household_notifications',
} as const;

export type UserProfileCardType =
  (typeof USER_PROFILE_CARDS)[keyof typeof USER_PROFILE_CARDS];

// ============================================================================
// QUESTIONNAIRE SUMMARY CARD TYPES
// ============================================================================

export const QUESTIONNAIRE_SUMMARY_CARDS = {
  CALCULATIONS: 'calculations',
} as const;

export type QuestionnaireSummaryCardType =
  (typeof QUESTIONNAIRE_SUMMARY_CARDS)[keyof typeof QUESTIONNAIRE_SUMMARY_CARDS];

// ============================================================================
// INTERFACES
// ============================================================================

export interface NavigationEventProperties {
  page?: string;
  section?: string;
  subsection?: string;
  previous_page?: string;
  navigation_method?:
    | 'header'
    | 'sidebar'
    | 'breadcrumb'
    | 'direct'
    | 'back_button';
  navigation_item?: string;
  icon_name?: string;
  icon_type?: string;
  action?: string;
  source?: 'header_icon' | 'sidebar' | 'direct';
  header_icon_name?:
    | 'account'
    | 'notifications'
    | 'watchlist'
    | 'search'
    | 'tour';
  user_role?: string;
  user_id?: string;
  user_permissions?: {
    canUseRealTimeNotificationsFileOwner?: boolean;
    canUseRealTimeNotificationsWatched?: boolean;
    canUseRealTimeNotificationsProperty?: boolean;
  };
}

export interface NotificationEventProperties {
  // Notification identification
  notification_id?: number;
  notification_type?: string;
  notification_type_category?: string;
  is_read?: boolean;
  is_watched?: boolean;
  is_single_notification?: boolean;
  is_bulk_notification?: boolean;
  is_property_notification?: boolean;
  is_system_notification?: boolean;

  // Action details
  action:
    | 'notification_click'
    | 'notification_view'
    | 'mark_read'
    | 'mark_unread'
    | 'select'
    | 'bulk_action'
    | 'filter_change'
    | 'pagination'
    | 'refresh'
    | 'help_click'
    | 'tour_click'
    | 'settings_click'
    | 'alert_dismiss'
    | 'bulk_details_open'
    | 'bulk_details_close'
    | 'expand'
    | 'collapse';

  // Filter and pagination context
  current_filters?: {
    type?: string;
    source?: string;
    read_status?: string;
    notification_type?: string;
  };
  current_page?: number;
  total_pages?: number;
  items_per_page?: number;
  total_notifications?: number;
  filtered_notifications?: number;

  // Bulk action context
  bulk_action_type?: 'mark_read' | 'mark_unread';
  selected_count?: number;
  total_selected?: number;

  // Card interaction context
  card_name?: string;
  card_expanded?: boolean;

  // Page context
  page_context: 'notification';
  detail_type: 'page' | 'tour' | 'header';

  // User context
  user_role?: 'compliance' | 'resident';
  user_permissions?: {
    canUseRealTimeNotificationsFileOwner?: boolean;
    canUseRealTimeNotificationsWatched?: boolean;
    canUseRealTimeNotificationsProperty?: boolean;
  };

  // Debug information
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface UserProfileEventProperties {
  // Card identification
  card_name: UserProfileCardType;
  card_display_name: string;

  // Card state
  card_expanded?: boolean;
  card_visible?: boolean;

  // Card-specific actions
  action_type:
    | 'help_click'
    | 'expand'
    | 'collapse'
    | 'save'
    | 'cancel'
    | 'setting_change'
    | 'password_reset'
    | 'email_change'
    | 'phone_change'
    | 'help_modal_open'
    | 'help_modal_close'
    | 'contact_email_add'
    | 'contact_email_delete'
    | 'contact_email_set_primary'
    | 'contact_phone_add'
    | 'contact_phone_delete'
    | 'contact_phone_set_primary';

  // Setting changes (for notification cards)
  setting_category?: 'in_app' | 'email' | 'communication_group' | 'contact';
  setting_name?: string;
  old_value?: unknown;
  new_value?: unknown;

  // Help interactions
  help_type?: 'tooltip' | 'modal' | 'info_box';
  help_content?: string;

  // Form interactions
  form_type?: 'account' | 'contact' | 'notification';
  form_success?: boolean;
  form_errors?: string[];

  // User context
  user_role?: 'compliance' | 'resident';
  user_permissions?: {
    canUseRealTimeNotificationsFileOwner?: boolean;
    canUseRealTimeNotificationsWatched?: boolean;
    canUseRealTimeNotificationsProperty?: boolean;
  };

  // Page context
  page_context?: 'user_settings' | 'other';

  // Detail type
  detail_type?: 'page' | 'tour';

  // Debug information
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface HeaderEventProperties {
  action_type: 'header_help_click' | 'header_tour_click';
  help_type?: 'header_help' | 'header_tour';
  page_context: 'user_settings' | 'other';
  detail_type?: 'page' | 'tour';
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface QuestionnaireSummaryEventProperties {
  // Card identification
  card_name: QuestionnaireSummaryCardType;
  card_display_name: string;

  // Card state
  card_expanded?: boolean;
  card_visible?: boolean;

  // Card-specific actions
  action_type: 'help_click' | 'help_modal_open' | 'help_modal_close';

  // Help interactions
  help_type?: 'tooltip' | 'modal' | 'info_box';
  help_content?: string;

  // User context
  user_role?: 'compliance' | 'resident';

  // Page context
  page_context?: 'questionnaire_summary' | 'certification_detail';

  // Detail type
  detail_type?: 'page' | 'tour';

  // Debug information
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface TourEventProperties {
  tour_name: string;
  tour_display_name: string;
  action:
    | 'start'
    | 'complete'
    | 'skip'
    | 'step_advance'
    | 'step_back'
    | 'pause'
    | 'resume'
    | 'auto_advance'
    | 'step_view'
    | 'close';
  step_number?: number;
  step_name?: string;
  step_selector?: string;
  step_category?: string;
  steps_completed?: number;
  total_steps?: number;
  tour_duration_ms?: number;
  step_duration_ms?: number;
  auto_play_enabled?: boolean;
  navigation_method?: 'manual' | 'auto' | 'keyboard' | 'click';
  page_context?: string;
  detail_type?: 'page' | 'tour';
  user_role?: string;
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface WatchListEventProperties {
  action:
    | 'add_to_watchlist'
    | 'remove_from_watchlist'
    | 'remove_all_from_watchlist';
  source: string;
  certification_ids?: number[];
  certification_count?: number;
  total_certifications_removed?: number;
  property_id?: number;
  household_id?: number;
  household_name?: string;
  user_role?: string;
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

export interface BulkActionsEventProperties {
  action:
    | 'modal_open'
    | 'modal_close'
    | 'update_start'
    | 'update_success'
    | 'update_failure'
    | 'selector_change'
    | 'help_dismiss'
    | 'help_show';

  // Bulk operation context
  certification_count?: number;
  certification_configs_count?: number;
  properties_count?: number;
  selected_certification_ids?: number[];

  // Status update context
  status_update_type?: 'bulk_status_update';
  previous_statuses?: Array<{
    certification_id: number;
    status_id: number;
    status_name: string;
  }>;
  new_statuses?: Array<{
    certification_id: number;
    status_id: number;
    status_name: string;
  }>;

  // File owner update context
  file_owner_update_type?: 'bulk_file_owner_update';
  property_id?: number;
  file_owner_id?: number | null;
  previous_file_owners?: Array<{
    certification_id: number;
    file_owner_id: number | null;
    file_owner_name: string | null;
  }>;
  new_file_owners?: Array<{
    certification_id: number;
    file_owner_id: number | null;
    file_owner_name: string | null;
  }>;

  // Success/failure context
  success_count?: number;
  failure_count?: number;
  error_message?: string;

  // User context
  user_role?: 'compliance' | 'resident';
  user_permissions?: {
    canDoBulkActions?: boolean;
  };

  // Page context
  page_context: string;
  detail_type: 'bulk_actions';

  // Debug information
  debug_info?: {
    component_name?: string;
    function_name?: string;
    timestamp?: string;
  };
}

// ============================================================================
// EVENT TYPES
// ============================================================================

// ENG-3571 (D2): vacancy tracker usage telemetry. Measures column/filter
// preferences (incl. Unit Status opt-out for B10), export, and fullscreen.
export const VACANCY_TRACKER_EVENTS = {
  VACANCY_COLUMN_TOGGLE: 'vacancy_tracker.column_toggle',
  VACANCY_FILTER_CHANGE: 'vacancy_tracker.filter_change',
  VACANCY_COLUMNS_APPLY: 'vacancy_tracker.columns_apply',
  VACANCY_COLUMNS_RESTORE: 'vacancy_tracker.columns_restore',
  VACANCY_EXPORT: 'vacancy_tracker.export',
  VACANCY_FULLSCREEN: 'vacancy_tracker.fullscreen',
} as const;

export type VacancyTrackerEventProperties = {
  property_id?: number | string;
  column_id?: string;
  visible?: boolean;
  filter_name?: string;
  detail_type?: 'vacancy_tracker';
};

export type EventName =
  | (typeof NAVIGATION_EVENTS)[keyof typeof NAVIGATION_EVENTS]
  | (typeof NOTIFICATION_EVENTS)[keyof typeof NOTIFICATION_EVENTS]
  | (typeof USER_PROFILE_EVENTS)[keyof typeof USER_PROFILE_EVENTS]
  | (typeof HEADER_EVENTS)[keyof typeof HEADER_EVENTS]
  | (typeof QUESTIONNAIRE_SUMMARY_EVENTS)[keyof typeof QUESTIONNAIRE_SUMMARY_EVENTS]
  | (typeof TOUR_EVENTS)[keyof typeof TOUR_EVENTS]
  | (typeof WATCH_LIST_EVENTS)[keyof typeof WATCH_LIST_EVENTS]
  | (typeof BULK_ACTIONS_EVENTS)[keyof typeof BULK_ACTIONS_EVENTS]
  | (typeof PARTNER_LISTING_EVENTS)[keyof typeof PARTNER_LISTING_EVENTS]
  | (typeof VACANCY_TRACKER_EVENTS)[keyof typeof VACANCY_TRACKER_EVENTS];

export type PartnerListingEventProperties = {
  listing_id?: number | string;
  property_name?: string;
  city?: string;
  neighborhood?: string;
  price_min?: number;
  rooms_available?: number;
  position_in_list?: number;
  destination_url?: string;
  referrer?: string;
  last_synced_at?: string;
};

export type EventProperties =
  | NavigationEventProperties
  | NotificationEventProperties
  | UserProfileEventProperties
  | HeaderEventProperties
  | QuestionnaireSummaryEventProperties
  | TourEventProperties
  | WatchListEventProperties
  | BulkActionsEventProperties
  | PartnerListingEventProperties
  | VacancyTrackerEventProperties;

export const createEventProperties = <T extends EventProperties>(
  properties: T
): T => properties;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getCardDisplayName = (cardName: UserProfileCardType): string => {
  const displayNames: Record<UserProfileCardType, string> = {
    account_information: 'Account Information',
    difference_explanation: 'Difference Explanation',
    contact_information: 'Contact Information',
    communication_preferences: 'Communication Preferences',
    daily_email_digest: 'Daily Email Digest',
    property_notifications: 'Property Notifications',
    file_owner_notifications: 'File Owner Notifications',
    watched_notifications: 'Watched Notifications',
  };
  return displayNames[cardName] || cardName;
};

export const getQuestionnaireSummaryCardDisplayName = (
  cardName: QuestionnaireSummaryCardType
): string => {
  const displayNames: Record<QuestionnaireSummaryCardType, string> = {
    calculations: 'Calculations',
  };
  return displayNames[cardName] || cardName;
};
